<template>
  <flow-part :id="$options.name" class="no-height">
    <template #heading>
      <h2>Jouw energieverbruik</h2>
    </template>
    <template #content>
      <div class="input-group vertical">
        <div class="household-helper">
          <span class="field-group-label">
            <template v-if="hideAmountInLabel">
              Aantal personen in jouw huishouden
            </template>
            <template v-else>
              <span class="amount"> {{ people }} </span> {{ peopleLabel }} in jouw huishouden
            </template>
          </span>

          <div class="household-buttons">
            <BigRadioInput id="household-1" v-model="comparisonStore.filters.householdSize" title="1" type="no-check"
              name="household-radio" :value="1" />
            <BigRadioInput id="household-2" v-model="comparisonStore.filters.householdSize" title="2"
              name="household-radio" type="no-check" :value="2" />
            <BigRadioInput id="household-3" v-model="comparisonStore.filters.householdSize" title="3" type="no-check"
              name="household-radio" :value="3" />
            <BigRadioInput id="household-4" v-model="comparisonStore.filters.householdSize" title="4"
              name="household-radio" type="no-check" :value="4" />
            <BigRadioInput id="household-5" v-model="comparisonStore.filters.householdSize" title="5+"
              name="household-radio" type="no-check" :value="5" />
          </div>
        </div>
        <div v-auto-animate class="input-group horizontal electricity-field">
          <TextInput id="electricity-input" v-model.number.lazy="comparisonStore.filters.electricity"
            label="Stroom (normaal)" input-mode="numeric" type="number"
            :show-valid="isFieldValid('electricity', !!comparisonStore.filters.electricity)" @keydown="numbersOnly"
            @input="handleInputChange('electricity', $event.target.value)" />

          <TextInput v-if="comparisonStore.filters.doubleMeter" id="electricity-off-peak-input"
            v-model.number.lazy="comparisonStore.filters.electricityOffPeak" label="Stroom (dal)" input-mode="numeric"
            type="number"
            :show-valid="isFieldValid('electricityOffPeak', !!comparisonStore.filters.electricityOffPeak)"
            @keydown="numbersOnly" @input="handleInputChange('electricityOffPeak', $event.target.value)" />
        </div>

        <CheckboxInput id="smart-meter-input" v-model="comparisonStore.filters.doubleMeter" :value="true">
          Ik heb een slimme meter
        </CheckboxInput>

        <step-info-toggle title="Heb ik een slimme meter?" class="smart-meter" scroll-align="start">
          <template #content>
            <div id="smart-meter-info">
              <section>
                Alleen met een slimme meter of digitale meter wordt je normaal- en dalverbruik geregistreerd (sinds
                2021). Met een analoge meter wordt geen dalverbruik meer geregistreerd.
              </section>
              <section>
                <div class="img">
                  <img :src="smartMeterImage" alt="slimme meter">
                </div>
                <div class="text">
                  <p class="question">
                    Slimme (digitale) meter
                  </p>
                  <p>Een slimme meter of digitale meter herken je aan het digitale display.</p>
                </div>
              </section>
              <section>
                <div class="img">
                  <img :src="dumbMeterImage" alt="analoge meter">
                </div>
                <div class="text">
                  <p class="question">
                    Analoge meter
                  </p>
                  <p>Een meter met een ronddraaiende schijf en een telwerk</p>
                </div>
              </section>
            </div>
          </template>
        </step-info-toggle>


        <div class="divider small"> </div>
        <div>
          <div v-auto-animate>
            <div v-if="comparisonStore.compareLocation && !comparisonStore.compareLocation.has_gas"
              class="card wizard-tip">
              <strong>Geen gasaansluiting</strong>
              <p>Op basis van je adres is er geen gasaansluiting. Als dit niet klopt, kun je dat hieronder aangeven.
              </p>
            </div>
          </div>
          <CheckboxInput id="has-gas-input" v-model="comparisonStore.filters.noGas" :value="true" :true-value="false"
            :false-value="true">
            Ik heb gas
          </CheckboxInput>



          <div class="input-group gas-field" v-if="!comparisonStore.filters.noGas">
            <TextInput v-model.number.lazy="comparisonStore.filters.gas" label="Gas" inputmode="numeric"
              placeholder="Aantal" type="number" :class="gasFieldStatus" :show-valid="!!comparisonStore.filters.gas" />
          </div>
        </div>



        <div class="divider small"></div>
        <CheckboxInput id="has-solar-input" v-model="comparisonStore.filters.electricityGenerated" :value="true">
          Ik heb zonnepanelen
        </CheckboxInput>

        <section v-if="comparisonStore.filters.electricityGenerated">
          <div class="input-group horizontal">
            <section class="solarpanel-field">
              <TextInput id="solar-panel-type-input" v-model="comparisonStore.filters.solarPanelAmount"
                label="Aantal panelen" type="number" :show-valid="!!comparisonStore.filters.solarPanelAmount"
                input-mode="numeric" />
            </section>
            <section class="electricity-field">
              <TextInput id="electricity-generation-input"
                v-model.number.lazy="comparisonStore.filters.electricityGeneration" label="Totale teruglevering"
                type="number" input-mode="numeric" :show-valid="!!comparisonStore.filters.electricityGeneration"
                @keydown="numbersOnly" />
            </section>
          </div>
          <step-info-toggle title="Wat moet ik invullen?">
            <template #content>
              Heb je zonnepanelen, dan kun je het aantal panelen opgeven. Wij rekenen dan uit hoeveel je waarschijnlijk
              teruglevert aan het net (op basis van het gemiddelde in Nederland).
              <br><br>
              Liever een nauwkeurige berekening?<br>
              Vul dan de totale hoeveelheid stroom in die je teruglevert aan het net. Deze hoeveelheid kun je vinden op
              de jaarafrekening van je energieleverancier.
              <br><br>
              In de vergelijking rekenen we automatisch uit hoeveel je saldeert en welke terugleverkosten je moet
              betalen.
            </template>
          </step-info-toggle>
        </section>
      </div>
    </template>
  </flow-part>
</template>

<script>
import Icon from '../../../../vue-elements/icon/Icon.vue';
import WizardStepMixin from '../../../../vue-elements/wizard/lib/WizardStepMixin.js';
import StepInfoToggle from '../StepInfoToggle.vue';
import FlowPart from '../FlowPart.vue';
import { useEnergyComparisonStore } from '../../stores/energyComparison.js';
import TextInput from '../../../../vue-elements/input/TextInput.vue';
import BigRadioInput from '../../../../vue-elements/input/BigRadioInput.vue';
import CheckboxInput from '../../../../vue-elements/input/CheckboxInput.vue';
import FieldLabel from '../../../../vue-elements/field/FieldLabel.vue';
import Tooltip from '../../../../../abstracts/elements/tooltip/tooltip.vue';

export default {
  name: 'HelpInputStep',
  mixins: [WizardStepMixin()],
  props: {
    hideAmountInLabel: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CheckboxInput,
    BigRadioInput,
    TextInput,
    FlowPart,
    Icon,
    StepInfoToggle,
    FieldLabel,
  },
  setup() {
    const comparisonStore = useEnergyComparisonStore();

    return {
      comparisonStore,
    };
  },
  data() {
    return {
      changedValues: {},
      timeout: null,
    };
  },
  computed: {
    dumbMeterImage() {
      return '/assets/vectors/analog-meter.svg';
    },
    smartMeterImage() {
      return '/assets/vectors/smart-meter.svg';
    },
    valid() {
      return (
        this.comparisonStore.filters.electricity !== null
        && this.comparisonStore.filters.gas !== null
        && (this.comparisonStore.filters.doubleMeter
          ? this.comparisonStore.filters.electricityOffPeak !== null
          : true)
        && (this.comparisonStore.filters.electricityGenerated
          ? this.comparisonStore.filters.electricityGeneration !== null
          : true)
      );
    },
    energyLabel() {
      if (this.comparisonStore.filters.doubleMeter) {
        return 'Stroomverbruik kWh (normaal)';
      }
      return 'Stroomverbruik kWh';
    },
    electricityFieldStatus() {
      return this.getNumberFieldStatus(this.comparisonStore.filters.electricity);
    },
    electricityOffPeakFieldStatus() {
      return this.getNumberFieldStatus(this.comparisonStore.filters.electricityOffPeak);
    },
    gasFieldStatus() {
      return this.getNumberFieldStatus(this.comparisonStore.filters.gas);
    },
    peopleLabel() {
      return this.comparisonStore.filters.householdSize === 1 ? 'persoon' : 'personen';
    },
    people() {
      switch (this.comparisonStore.filters.householdSize) {
        case 1:
          return 'Eén';
        case 2:
          return 'Twee';
        case 3:
          return 'Drie';
        case 4:
          return 'Vier';
        case 5:
          return 'Vijf of meer';
        default:
          return 'Aantal';
      }
    },
  },
  watch: {
    'comparisonStore.filters.householdSize': {
      handler(value) {
        this.setEstimate(value);
        this.logAnalyticsEvent('compare-energy.wizard-step-complete', {
          step: this.$options.name,
          value,
        });
      },
      immediate: false,
    },
    'comparisonStore.filters.solarPanelAmount': {
      handler(value) {
        this.setSolarPowerEstimate(value);
      },
      immediate: false,
    },
    'comparisonStore.filters.doubleMeter': {
      handler(newVal) {
        if (!newVal) {
          this.comparisonStore.filters.electricity += this.comparisonStore.filters.electricityOffPeak;
          this.comparisonStore.filters.electricityOffPeak = 0;
        } else {
          this.comparisonStore.filters.electricityOffPeak = Math.floor(this.comparisonStore.filters.electricity / 2);
          this.comparisonStore.filters.electricity = Math.ceil(this.comparisonStore.filters.electricity / 2);
        }
      },
    },
    'comparisonStore.filters.heatingType': {
      handler() {
        this.setEstimate(this.comparisonStore.filters.householdSize);
      },
    },
    'comparisonStore.filters.electricityGenerated': function (val) {
      this.setSolarPowerEstimate(this.comparisonStore.filters.solarPanelAmount);
    },
    'comparisonStore.filters.noGas': function (val) {
      this.setGastEstimate(this.comparisonStore.filters.householdSize);
    },
  },
  mounted() {
    this.focusNextInputOnEnter();
  },
  methods: {
    handleInputChange(field, value) {
      this.changedValues[field] = true;
      this.comparisonStore.filters[field] = value;
    },
    isFieldValid(field, initialValueCondition) {
      return initialValueCondition && this.changedValues[field];
    },
    numbersOnly(event) {
      if (
        (!!event.key.match(/[\W]/g) && event.target.value.length < 7)
        || (!(event.target.value.length < 7)
          && ![
            'Backspace',
            'Enter',
            'Control',
            'ArrowLeft',
            'ArrowRight',
            'Meta',
            'Delete',
          ].includes(event.key))
      ) {
        event.preventDefault();
      }
    },
    validate() {
      this.status = this.valid ? 'complete' : 'invalid';
    },
    focusNextInputOnEnter() {
      this.$el.querySelectorAll('input[type="number"]')
        .forEach((element, index, array) => {
          element.addEventListener('keydown', (event) => {
            if (event.keyCode === 13 && event.target.nodeName === 'INPUT') {
              event.preventDefault();

              let nextElementIndex = null;
              array.forEach((el, index) => {
                if (element === el) {
                  nextElementIndex = index + 1;
                }
              });

              ((nextElementIndex !== null && array.item(nextElementIndex)) ? array.item(nextElementIndex)
                .focus() : element.blur());
            }
          });
        });
    },
    setEstimate(estimate) {
      if (!this.comparisonStore.filters.noGas) {
        this.setGastEstimate(estimate);
      }
      switch (estimate) {
        case 1:
          this.comparisonStore.filters.electricity = 750;
          this.comparisonStore.filters.electricityOffPeak = 750;
          break;
        case 2:
          this.comparisonStore.filters.electricity = 1250;
          this.comparisonStore.filters.electricityOffPeak = 1250;
          break;
        case 3:
          this.comparisonStore.filters.electricity = 1750;
          this.comparisonStore.filters.electricityOffPeak = 1750;
          break;
        case 4:
          this.comparisonStore.filters.electricity = 2250;
          this.comparisonStore.filters.electricityOffPeak = 2250;
          break;
        case 5:
          this.comparisonStore.filters.electricity = 2500;
          this.comparisonStore.filters.electricityOffPeak = 2500;
          break;
        default:
          this.comparisonStore.filters.electricity = 1250;
          this.comparisonStore.filters.electricityOffPeak = 1250;
      }

      this.changedValues.electricity = true;
      this.changedValues.electricityOffPeak = true;
      this.clearUnselectedFilters();
    },

    setGastEstimate(estimate) {
      switch (estimate) {
        case 1:
          this.comparisonStore.filters.gas = 900;
          break;
        case 2:
          this.comparisonStore.filters.gas = 1000;
          break;
        case 3:
          this.comparisonStore.filters.gas = 1500;
          break;
        case 4:
          this.comparisonStore.filters.gas = 1700;
          break;
        case 5:
          this.comparisonStore.filters.gas = 1800;
          break;
        default:
          this.comparisonStore.filters.gas = 1000;
      }
      this.changedValues.gas = true;
    },
    clearUnselectedFilters() {
      if (this.comparisonStore.filters.noGas) {
        this.comparisonStore.filters.gas = 0;
      }
      if (!this.comparisonStore.filters.doubleMeter) {
        this.comparisonStore.filters.electricity += this.comparisonStore.filters.electricityOffPeak;
        this.comparisonStore.filters.electricityOffPeak = 0;
      }
      if (!this.comparisonStore.filters.electricityGenerated) {
        this.comparisonStore.filters.electricityGeneration = 0;
      }
      if (this.comparisonStore.filters.noGas && this.comparisonStore.filters.heatingType === 'heatPump') {
        if (this.comparisonStore.filters.doubleMeter) {
          this.comparisonStore.filters.electricity += 1400;
          this.comparisonStore.filters.electricityOffPeak += 1400;
        }
        if (!this.comparisonStore.filters.doubleMeter) {
          this.comparisonStore.filters.electricity += 2800;
        }
      }
    },
    setSolarPowerEstimate(estimate) {
      if (this.comparisonStore.filters.electricityGenerated) {
        this.comparisonStore.filters.electricityGeneration = estimate * 270;
      } else {
        this.comparisonStore.filters.electricityGeneration = 0;
      }
    },
    getNumberFieldStatus(fieldValue) {
      if (fieldValue < 0) {
        return 'field-error';
      }
      if (fieldValue === 0 || fieldValue === '') {
        return 'field-empty';
      }
      return 'field-valid';
    },
  },
};
</script>

<style lang="scss">
@use "../../../../../assets/scss/_helpers/display";

// Manual scoping of styles
#HelpInputStep {
  .step-info.smart-meter {
    margin-top: 0px;
  }

  .step-info.estimation {
    margin-top: 0px;
  }

  .field-group.gas-field.has-gas {
    margin-bottom: 16px;
  }

  .divider-no-margin {
    margin: 0;
    margin-bottom: 12px;
  }

}

@media screen and (max-height: 880px) {
  .short-input-container {
    padding-top: 18px !important;
  }
}

@media screen and (max-height: 880px) and (min-width: 1024px) {
  .short-input-container .part-content h2 {
    font-size: 24px;
    font-weight: 600;
    line-height: 32px;
    margin-bottom: 8px;
  }

  .divider-no-margin {
    margin-bottom: 4px;
    margin-top: -4px;
  }
}

.household-helper {
  margin-bottom: 16px;
}

#smart-meter-info {
  @include display.flex-col-start();

  section {
    @include display.flex-row-between();

    align-items: flex-start;
    padding: 8px 0;

    &:first-of-type {
      padding-top: 0;
    }

    &:last-of-type {
      padding-bottom: 0;
    }
  }

  .img {
    min-width: 64px;
    width: 64px;

    img {
      width: 100%;
    }
  }

  .text {
    margin-left: 16px;
  }
}
</style>
